import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";

import { cn } from "@/lib/utils";

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 rounded-md text-[0.8125rem] sm:text-sm font-semibold ring-offset-background transition-[transform,background-color,border-color,box-shadow,color] duration-200 ease-out focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0 max-w-full tracking-wide uppercase leading-normal lg:whitespace-nowrap hover:scale-[1.02] active:scale-[0.98]",
  {
    variants: {
      variant: {
        default: "bg-primary text-primary-foreground hover:bg-primary/90",
        destructive: "bg-destructive text-destructive-foreground hover:bg-destructive/90",
        outline: "border border-input bg-background hover:bg-accent hover:text-accent-foreground",
        secondary: "bg-secondary text-secondary-foreground hover:bg-secondary/80",
        ghost: "hover:bg-accent hover:text-accent-foreground",
        link: "text-primary underline-offset-4 hover:underline",
        // Cosmic theme variants
        gold: "bg-gradient-gold text-background font-semibold hover:shadow-glow-gold hover:-translate-y-0.5",
        mantis: "bg-gradient-mantis text-background font-semibold hover:shadow-glow-green hover:-translate-y-0.5",
        cosmic: "bg-gradient-cosmic text-foreground border border-gold/30 hover:border-gold/60 hover:shadow-glow-gold hover:-translate-y-0.5",
        "ghost-gold": "text-gold hover:bg-gold/10 hover:text-gold",
        "outline-gold": "border-2 border-gold text-gold bg-transparent hover:bg-gold hover:text-background",
        "outline-mantis": "border-2 border-secondary text-secondary bg-transparent hover:bg-secondary hover:text-background",
      },
      size: {
        default: "h-12 px-5 py-2.5 min-h-[48px]",
        sm: "h-10 rounded-md px-4 min-h-[44px]",
        lg: "h-14 rounded-md px-5 sm:px-8 text-base min-h-[56px]",
        xl: "h-16 rounded-lg px-6 sm:px-10 text-base sm:text-lg min-h-[64px]",
        icon: "h-12 w-12 min-h-[48px] min-w-[48px]",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  },
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : "button";
    return <Comp className={cn(buttonVariants({ variant, size, className }))} ref={ref} {...props} />;
  },
);
Button.displayName = "Button";

export { Button, buttonVariants };
